import React, { useEffect } from "react";
import { Route, Switch as SwitchRouter } from "react-router-dom";
import * as Scrollbar from "@/common/scrollbar";
import * as UnifiedTimeline from "../pages/decisions/DecisionDashboard/Reports/Details/UnifiedTimeline";
import { AsyncRoute } from "./AsyncRoute";
import { CtxMenuRootComp } from "../redux/contextMenu/Comp";
import { DocumentEditors } from "@/app/providers/services";
import { AppSidebar, useAppSidebar } from "../common/Sidebar/AppSidebar";
import styled from "@emotion/styled";
import { useTheme } from "@/app/providers/ThemeContext";
import { Header } from "@/ui/blocks/Header";
import { FEATURE_SETTINGS_CHANGED_EVENT, isFeatureOrParentEnabled } from "@/config/features";
import { AdapterWorkflowPanel, isAdapterWorkflowPath } from "@/features/adapters";

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 3.5rem);
  width: 100%;
  border-top-left-radius: 1.5rem;
  background-color: ${props => props.theme.mixins.body.background};
  position: relative;
  overflow: hidden;
`;

export interface DecisilveyLayoutProps {
  page?: React.ReactNode;
  routes?: any[];
  childRoutes?: any[];
  location?: any;
  match?: any;
}

const DecisivelyLayoutNonCompact = (props: DecisilveyLayoutProps) => {
  // pulling routes as we dont want it in props as we unpack into sidebar
  const { page, routes, childRoutes, location, match } = props;
  const [, refreshFeatureSettings] = React.useReducer((value) => value + 1, 0);
  React.useEffect(() => {
    window.addEventListener(FEATURE_SETTINGS_CHANGED_EVENT, refreshFeatureSettings);
    return () => window.removeEventListener(FEATURE_SETTINGS_CHANGED_EVENT, refreshFeatureSettings);
  }, []);
  const visibleChildRoutes = (childRoutes ?? []).filter((route) => !route.feature || isFeatureOrParentEnabled(route.feature));
  const showAdapterWorkflow = isAdapterWorkflowPath(location?.pathname ?? "");
  const { setTheme, setAppearance } = useTheme();

  // This layout does not support new theme options — force classic + light
  useEffect(() => {
    setTheme("light");
    setAppearance("classic");
  }, []);

  return (
    <AppSidebar className="h-full bg-sidebar" routes={visibleChildRoutes} location={location} match={match}>
      <Scrollbar.Style />

      <UnifiedTimeline.Style />

      {/* defined here as its not really required app wide */}
      <DocumentEditors />

      {/* Header and content on the right */}
      <div className="flex flex-col flex-1 h-full w-full overflow-hidden">
        <AppHeader routes={visibleChildRoutes} />
        <AppContent>
          <div className="relative flex min-h-0 flex-1">
            <div className="flex min-w-0 flex-1 flex-col">
              <AsyncRoute>
                {childRoutes ? (
                  <SwitchRouter>
                    {visibleChildRoutes.map(route => (
                      <Route key={route.path} {...route} />
                    ))}
                  </SwitchRouter>
                ) : (
                  page
                )}
              </AsyncRoute>
            </div>
            {showAdapterWorkflow && <AdapterWorkflowPanel />}
          </div>
        </AppContent>
      </div>

      <CtxMenuRootComp />
    </AppSidebar>
  );
};

const AppHeader = ({ routes }: { routes: any[] }) => {
  const { toggleSidebar } = useAppSidebar();
  return <Header routes={routes} onToggleSidebar={toggleSidebar} />;
};

export default DecisivelyLayoutNonCompact;
